/**
 * AbilityIconRegistry — authored SVG icons for all canonical calling unlocks.
 * Preserves the original starter kits + first unlocks (HUD-restyle Lane C). Mirrors `mobs/MobSVGRegistry.ts`: a registry
 * that maps an ability id to a per-family generator invocation (motif + school
 * tint + variant), NOT dozens of loose hand-authored SVG files.
 *
 * Coverage = every canonical curve unlock (`ABILITY_UNLOCK_LEVELS` in
 * hotbars.gen.ts — the SSOT; ids never invented).
 * Schools come from `abilities.gen.ts`. Anything not covered here keeps its
 * emoji fallback (ActionBarSlot renders the emoji when `icon` isn't a path).
 *
 * Consumers:
 *   - `scripts/build-ability-icons.ts` writes each icon to
 *     `public/icons/abilities/<id>.svg` (served at `/icons/abilities/<id>.svg`).
 *   - `registration/applyStarterIcons.ts` sets `icon` on the ability metadata to
 *     that path, so the hotbar `<img>` seam (`icon.startsWith('/')`) picks it up.
 */

import { CURVE_ABILITY_ICON_SPECS } from './CurveAbilityIconSpecs';
export { CURVE_ICON_CLASS_GROUPS } from './CurveAbilityIconSpecs';
import { svgToBase64 } from '../svg/SVGUtils';
import { schoolPalette, type IconSchool } from './iconPalette';
import { MOTIF_FNS, type Motif, type MotifVariant } from './motifs';

/** Public path an authored icon is served from (Vite serves `public/` at root). */
export const ABILITY_ICON_DIR = '/icons/abilities';
export const abilityIconPath = (id: string): string => `${ABILITY_ICON_DIR}/${id}.svg`;

interface IconSpec {
  motif: Motif;
  /** School family for the tint. Usually the ability's `school`; a heal ability
   *  can be tinted 'healing' regardless of school for a clearer read. */
  school: IconSchool;
  variant?: MotifVariant;
}

const SPECS = new Map<string, IconSpec>();

function register(id: string, motif: Motif, school: IconSchool, variant?: MotifVariant): void {
  SPECS.set(id, { motif, school, variant });
}

// ── Warrior (physical) ──────────────────────────────────────────────────────
register('warrior_auto', 'blade', 'physical');
register('mortal_strike', 'blade', 'physical', 'heavy');
register('whirlwind', 'whirl', 'physical');
register('charge', 'dash', 'physical');
register('execute', 'blade', 'physical', 'down');
register('bladestorm', 'whirl', 'physical', 'knives');

// ── Mage (arcane / fire / frost) ─────────────────────────────────────────────
register('mage_auto', 'orb', 'arcane');
register('fireball', 'orb', 'fire');
register('frost_bolt', 'orb', 'frost', 'crystal');
register('blink', 'dash', 'arcane'); // shared id (mage + rogue starter slot 4)
register('blizzard', 'nova', 'frost', 'shard');
register('meteor', 'nova', 'fire', 'rock');

// ── Hunter (physical / fire) ──────────────────────────────────────────────────
register('ranger_auto', 'arrow', 'physical');
register('aimed_shot', 'arrow', 'physical', 'aimed');
register('multiple_arrows', 'multiArrow', 'physical');
register('disengage', 'dash', 'physical', 'back');
register('rain_of_arrows', 'multiArrow', 'physical', 'rain');
register('explosive_shot', 'arrow', 'fire', 'fire');

// ── Paladin (holy) ────────────────────────────────────────────────────────────
register('paladin_auto', 'blade', 'holy');
register('crusader_strike', 'blade', 'holy', 'cross');
register('divine_storm', 'whirl', 'holy');
register('divine_steed', 'dash', 'holy');
register('consecration', 'nova', 'holy', 'ground');
register('hammer_of_wrath', 'hammer', 'holy');

// ── Rogue (physical) ──────────────────────────────────────────────────────────
register('assassin_auto', 'dagger', 'physical');
register('backstab', 'dagger', 'physical', 'back');
register('fan_of_knives', 'whirl', 'physical', 'knives');
// rogue slot 4 is 'blink' — already registered (shared).
register('eviscerate', 'dagger', 'physical', 'crit');
register('assassinate', 'dagger', 'physical', 'fatal');

// ── Shaman (nature / fire) ────────────────────────────────────────────────────
register('shaman_auto', 'blade', 'nature');
register('lightning_bolt', 'bolt', 'nature'); // shaman starter (nature); shared id
register('flame_shock', 'orb', 'fire', 'ember');
register('healing_stream_totem', 'totem', 'nature');
register('searing_totem', 'totem', 'fire', 'fire');
register('stormstrike', 'bolt', 'nature', 'strike');

// ── Priest (holy / shadow) ────────────────────────────────────────────────────
register('priest_auto', 'orb', 'holy');
register('flash_mend', 'heal', 'holy');
register('shadow_dart', 'orb', 'shadow');
register('word_of_shielding', 'shield', 'holy');
register('renew', 'heal', 'holy', 'hot');
register('greater_mend', 'heal', 'holy', 'greater');

// ── Warlock (shadow / fire) ───────────────────────────────────────────────────
register('warlock_auto', 'orb', 'shadow');
register('corruption', 'curse', 'shadow', 'gloammark');
register('immolate', 'orb', 'fire', 'cinder_scar');
register('curse_of_agony', 'curse', 'shadow', 'last_toll');
register('drain_life', 'orb', 'shadow', 'drain');
register('death_coil', 'curse', 'shadow', 'coil');

// ── Druid (nature) ────────────────────────────────────────────────────────────
register('druid_auto', 'orb', 'nature');
register('wrath', 'orb', 'nature', 'spike');
register('moonfire', 'orb', 'nature', 'moon');
register('entangling_roots', 'roots', 'nature');
register('rejuvenation', 'heal', 'nature', 'leaf');
register('healing_touch', 'heal', 'nature', 'leafBig');

// Preserve original starter registration order and art. Additional curve entries
// are composed afterward so existing generated SVG bytes remain stable.
for (const spec of CURVE_ABILITY_ICON_SPECS) register(spec.id, spec.motif, spec.school, spec.variant);

// Off-curve class movement abilities (plan 04a, D13) — taught by the movement
// lesson quest. Registered last for the same byte-stability reason.
register('stormstride', 'dash', 'nature', 'bolt');
register('harebound', 'dash', 'nature', 'hop');
register('pact_door', 'dash', 'shadow', 'door');
register('lightfall', 'dash', 'holy', 'float');
// Hunter movement (D22) — registered last for the same byte-stability reason.
register('tetherline', 'dash', 'physical', 'tether');
// Priest friendly speed buff (lane AM, 2026-10-08) — registered last for the same reason.
register('brightstep', 'dash', 'holy', 'step');
// Shared Hush Nook movement reward (Chapter 16) — warm leaping skip with terminal light bead.
register('lanternskip', 'dash', 'holy', 'skip');

// ── Public API ────────────────────────────────────────────────────────────────

/** True if an authored icon exists for this ability id. */
export function hasAbilityIcon(id: string): boolean {
  return SPECS.has(id);
}

/** Every ability id with an authored icon (deterministic insertion order). */
export function abilityIconIds(): string[] {
  return Array.from(SPECS.keys());
}

/** Raw SVG string for an ability icon, or undefined if not covered. */
export function generateAbilityIconSVG(id: string, size = 64): string | undefined {
  const spec = SPECS.get(id);
  if (!spec) return undefined;
  return MOTIF_FNS[spec.motif](schoolPalette(spec.school), size, spec.variant);
}

/** Base64 data-URL for an ability icon (parity with mob registry API). */
export function generateAbilityIconDataUrl(id: string, size = 64): string | undefined {
  const svg = generateAbilityIconSVG(id, size);
  return svg ? svgToBase64(svg) : undefined;
}

/** Class grouping for contact sheets / review (starter-kit order, ≤Lv 12). */
export const ICON_CLASS_GROUPS: Record<string, string[]> = {
  warrior: ['warrior_auto', 'mortal_strike', 'whirlwind', 'charge', 'execute', 'bladestorm'],
  mage: ['mage_auto', 'fireball', 'frost_bolt', 'blink', 'blizzard', 'meteor'],
  hunter: ['ranger_auto', 'aimed_shot', 'multiple_arrows', 'disengage', 'rain_of_arrows', 'explosive_shot'],
  paladin: ['paladin_auto', 'crusader_strike', 'divine_storm', 'divine_steed', 'consecration', 'hammer_of_wrath'],
  rogue: ['assassin_auto', 'backstab', 'fan_of_knives', 'blink', 'eviscerate', 'assassinate'],
  shaman: ['shaman_auto', 'lightning_bolt', 'flame_shock', 'healing_stream_totem', 'searing_totem', 'stormstrike'],
  priest: ['priest_auto', 'flash_mend', 'shadow_dart', 'word_of_shielding', 'renew', 'greater_mend'],
  warlock: ['warlock_auto', 'corruption', 'immolate', 'curse_of_agony', 'drain_life', 'death_coil'],
  druid: ['druid_auto', 'wrath', 'moonfire', 'entangling_roots', 'rejuvenation', 'healing_touch'],
};
